import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import './SendFile.css';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3003/api/v1';

const SendFile = () => {
    const [file, setFile] = useState(null);
    const [xmlContent, setXmlContent] = useState('');
    const [previewResult, setPreviewResult] = useState(null);
    const [sendResult, setSendResult] = useState(null);
    const [loading, setLoading] = useState(false);
    const [sending, setSending] = useState(false);
    const [error, setError] = useState(null);
    const fileInputRef = useRef(null);
    const navigate = useNavigate();

    const getAuthHeaders = () => ({
        'Authorization': `Bearer ${localStorage.getItem('token')}`,
        'Content-Type': 'application/json'
    });

    const handleFileSelect = (e) => {
        const selectedFile = e.target.files[0];
        if (!selectedFile) return;

        if (!selectedFile.name.toLowerCase().endsWith('.xml')) {
            setError('Only XML files are supported');
            return;
        }

        setFile(selectedFile);
        setError(null);
        setPreviewResult(null);
        setSendResult(null);

        const reader = new FileReader();
        reader.onload = (event) => {
            setXmlContent(event.target.result);
        };
        reader.readAsText(selectedFile);
    };

    const handleDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();
        const droppedFile = e.dataTransfer.files[0];
        if (droppedFile) {
            if (!droppedFile.name.toLowerCase().endsWith('.xml')) {
                setError('Only XML files are supported');
                return;
            }
            setFile(droppedFile);
            setError(null);
            setPreviewResult(null);
            setSendResult(null);

            const reader = new FileReader();
            reader.onload = (event) => {
                setXmlContent(event.target.result);
            };
            reader.readAsText(droppedFile);
        }
    };

    const handleDragOver = (e) => {
        e.preventDefault();
        e.stopPropagation();
    };

    const handlePreview = async () => {
        if (!xmlContent) return;

        setLoading(true);
        setError(null);
        setPreviewResult(null);
        setSendResult(null);

        try {
            const response = await fetch(`${API_BASE_URL}/as2/preview-route`, {
                method: 'POST',
                headers: getAuthHeaders(),
                body: JSON.stringify({ xml: xmlContent })
            });

            const data = await response.json();

            if (!response.ok) {
                setError(data.message || 'Failed to preview route');
                setPreviewResult(data);
            } else {
                setPreviewResult(data);
            }
        } catch (err) {
            setError(err.message || 'Network error');
        } finally {
            setLoading(false);
        }
    };

    const handleSend = async () => {
        if (!xmlContent || !previewResult?.success) return;

        setSending(true);
        setError(null);
        setSendResult(null);

        try {
            const response = await fetch(`${API_BASE_URL}/as2/send`, {
                method: 'POST',
                headers: getAuthHeaders(),
                body: JSON.stringify({
                    xml: xmlContent,
                    filename: file?.name || 'manual-upload.xml'
                })
            });

            const data = await response.json();

            if (!response.ok) {
                setError(data.message || 'Failed to send file');
            } else {
                setSendResult(data);
            }
        } catch (err) {
            setError(err.message || 'Network error');
        } finally {
            setSending(false);
        }
    };

    const handleClear = () => {
        setFile(null);
        setXmlContent('');
        setPreviewResult(null);
        setSendResult(null);
        setError(null);
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    const renderValidationChecks = (checks) => {
        if (!checks) return null;
        return (
            <div className="sf-checks">
                {checks.map((check, idx) => (
                    <div key={idx} className={`sf-check-item sf-check-${check.status}`}>
                        <span className="sf-check-icon">
                            {check.status === 'pass' ? '\u2713' : check.status === 'warn' ? '!' : '\u2717'}
                        </span>
                        <span className="sf-check-label">{check.label}</span>
                        <span className="sf-check-value">{check.value || ''}</span>
                    </div>
                ))}
            </div>
        );
    };

    return (
        <>
            <header className="top-header">
                <div className="breadcrumb">
                    <span>Tools</span>
                    <span className="breadcrumb-separator">/</span>
                    <span className="breadcrumb-current">Send File</span>
                </div>
            </header>
            <div className="sf-page">
                <div className="page-header">
                    <div>
                        <h1>Send EPCIS File</h1>
                        <p className="subtitle">Upload an EPCIS XML file, preview routing, validate partners, and send via AS2</p>
                    </div>
                </div>

                <div className="sf-container">
                    {/* Step 1: Upload */}
                    <div className="sf-section">
                        <div className="sf-section-header">
                            <div className="sf-step-badge">1</div>
                            <h3>Upload EPCIS File</h3>
                        </div>

                        <div
                            className={`sf-dropzone ${file ? 'sf-dropzone-active' : ''}`}
                            onDrop={handleDrop}
                            onDragOver={handleDragOver}
                            onClick={() => fileInputRef.current?.click()}
                        >
                            <input
                                ref={fileInputRef}
                                type="file"
                                accept=".xml"
                                onChange={handleFileSelect}
                                hidden
                            />
                            {file ? (
                                <div className="sf-file-info">
                                    <svg className="sf-file-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24" width="32" height="32">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path>
                                    </svg>
                                    <div>
                                        <div className="sf-file-name">{file.name}</div>
                                        <div className="sf-file-size">{(file.size / 1024).toFixed(1)} KB</div>
                                    </div>
                                    <button
                                        className="sf-clear-btn"
                                        onClick={(e) => { e.stopPropagation(); handleClear(); }}
                                    >
                                        Remove
                                    </button>
                                </div>
                            ) : (
                                <div className="sf-dropzone-text">
                                    <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" width="40" height="40">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"></path>
                                    </svg>
                                    <p>Drag and drop an EPCIS XML file here, or click to browse</p>
                                    <span>Supports .xml files</span>
                                </div>
                            )}
                        </div>

                        {file && (
                            <div className="sf-actions">
                                <button
                                    className="sf-preview-btn"
                                    onClick={handlePreview}
                                    disabled={loading || !xmlContent}
                                >
                                    {loading ? 'Analyzing...' : 'Preview Route'}
                                </button>
                            </div>
                        )}
                    </div>

                    {/* Error Display */}
                    {error && (
                        <div className="sf-error">
                            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" width="20" height="20">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                            </svg>
                            <span>{error}</span>
                        </div>
                    )}

                    {/* Step 2: Route Preview */}
                    {previewResult && (
                        <div className="sf-section">
                            <div className="sf-section-header">
                                <div className="sf-step-badge">2</div>
                                <h3>Route Preview & Validation</h3>
                            </div>

                            <div className="sf-route-preview">
                                {/* Parsed identifiers */}
                                <div className="sf-parsed-info">
                                    <h4>Parsed Identifiers</h4>
                                    <div className="sf-id-grid">
                                        <div className="sf-id-card">
                                            <span className="sf-id-label">Sender</span>
                                            <span className="sf-id-value">{previewResult.sender?.name || 'Unknown'}</span>
                                            <span className="sf-id-sub">
                                                {previewResult.sender?.sgln || previewResult.sender?.gln || 'Not found'}
                                            </span>
                                        </div>
                                        <div className="sf-id-arrow">
                                            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" width="24" height="24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M14 5l7 7m0 0l-7 7m7-7H3"></path>
                                            </svg>
                                        </div>
                                        <div className="sf-id-card">
                                            <span className="sf-id-label">Receiver</span>
                                            <span className="sf-id-value">{previewResult.receiver?.name || 'Unknown'}</span>
                                            <span className="sf-id-sub">
                                                {previewResult.receiver?.sgln || previewResult.receiver?.gln || 'Not found'}
                                            </span>
                                        </div>
                                        <div className="sf-id-arrow">
                                            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" width="24" height="24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M14 5l7 7m0 0l-7 7m7-7H3"></path>
                                            </svg>
                                        </div>
                                        <div className="sf-id-card sf-id-connector">
                                            <span className="sf-id-label">AS2 Connector</span>
                                            <span className="sf-id-value">{previewResult.connector?.name || previewResult.connector?.id || 'None'}</span>
                                            <span className="sf-id-sub">
                                                {previewResult.connector?.source ? `via ${previewResult.connector.source.replace('_', ' ')}` : ''}
                                            </span>
                                        </div>
                                    </div>
                                </div>

                                {/* Validation checks */}
                                <div className="sf-validation">
                                    <h4>Validation Checks</h4>
                                    {renderValidationChecks(previewResult.checks)}
                                </div>

                                {/* EPCIS Info */}
                                {previewResult.epcisInfo && (
                                    <div className="sf-epcis-info">
                                        <h4>Document Info</h4>
                                        <div className="sf-info-grid">
                                            <div className="sf-info-item">
                                                <span className="sf-info-label">Events</span>
                                                <span className="sf-info-value">{previewResult.epcisInfo.eventCount || 0}</span>
                                            </div>
                                            <div className="sf-info-item">
                                                <span className="sf-info-label">EPCs</span>
                                                <span className="sf-info-value">{previewResult.epcisInfo.epcCount || 0}</span>
                                            </div>
                                            <div className="sf-info-item">
                                                <span className="sf-info-label">Version</span>
                                                <span className="sf-info-value">{previewResult.epcisInfo.schemaVersion || '-'}</span>
                                            </div>
                                            {previewResult.epcisInfo.lotNumber && (
                                                <div className="sf-info-item">
                                                    <span className="sf-info-label">Lot #</span>
                                                    <span className="sf-info-value">{previewResult.epcisInfo.lotNumber}</span>
                                                </div>
                                            )}
                                            {previewResult.epcisInfo.purchaseOrder && (
                                                <div className="sf-info-item">
                                                    <span className="sf-info-label">PO #</span>
                                                    <span className="sf-info-value">{previewResult.epcisInfo.purchaseOrder}</span>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                )}

                                {/* Send button */}
                                {previewResult.success && (
                                    <div className="sf-send-section">
                                        <button
                                            className="sf-send-btn"
                                            onClick={handleSend}
                                            disabled={sending}
                                        >
                                            {sending ? (
                                                <>
                                                    <span className="sf-spinner"></span>
                                                    Sending...
                                                </>
                                            ) : (
                                                <>
                                                    <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" width="18" height="18">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8"></path>
                                                    </svg>
                                                    Send via AS2
                                                </>
                                            )}
                                        </button>
                                        <p className="sf-send-hint">
                                            File will be sent to {previewResult.receiver?.name || 'receiver'} via connector {previewResult.connector?.name || previewResult.connector?.id}
                                        </p>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    {/* Step 3: Send Result */}
                    {sendResult && (
                        <div className="sf-section">
                            <div className="sf-section-header">
                                <div className="sf-step-badge sf-step-done">3</div>
                                <h3>Transmission Result</h3>
                            </div>

                            <div className={`sf-result ${sendResult.success ? 'sf-result-success' : 'sf-result-error'}`}>
                                <div className="sf-result-header">
                                    <span className="sf-result-icon">
                                        {sendResult.success ? '\u2713' : '\u2717'}
                                    </span>
                                    <span className="sf-result-text">
                                        {sendResult.success ? 'File Sent Successfully' : 'Send Failed'}
                                    </span>
                                </div>

                                {sendResult.success && (
                                    <div className="sf-result-details">
                                        <div className="sf-result-row">
                                            <span>Transfer ID</span>
                                            <code>{sendResult.transferId}</code>
                                        </div>
                                        <div className="sf-result-row">
                                            <span>Transmission Log</span>
                                            <code>#{sendResult.transmissionId}</code>
                                        </div>
                                        <div className="sf-result-row">
                                            <span>Status</span>
                                            <span className="sf-status-badge">TRANSFER_INITIATED</span>
                                        </div>
                                        <button
                                            className="sf-logs-btn"
                                            onClick={() => navigate('/file-transmissions')}
                                        >
                                            View in AS2 Logs →
                                        </button>
                                    </div>
                                )}

                                {!sendResult.success && sendResult.message && (
                                    <p className="sf-result-error-msg">{sendResult.message}</p>
                                )}
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </>
    );
};

export default SendFile;
